import {
  ApolloCache,
  ApolloError,
  BaseMutationOptions,
  MutationHookOptions,
  MutationUpdaterFunction,
  useApolloClient,
  useMutation,
} from "@apollo/client";
import {
  ADD_ACCOUNT_ROLES,
  ADD_CAREPROVIDER_ACCOUNT_ROLES,
  ADD_PRIORITY_PROVIDER,
  ARCHIVE_PATIENT,
  ARCHIVE_PROVIDER_SEARCH_PATIENT,
  CHECK_SEALD_SESSIONS,
  COMPLETE_PRODUCT_TOUR,
  CONNEXT_AUTHENTICATION,
  CREATE_ACCOUNT,
  CREATE_ACCOUNT_FOR_CARESEEKER,
  CREATE_AUCTION,
  CREATE_CAREPROVIDER,
  CREATE_CAREPROVIDER_REVERSE_SEARCH,
  CREATE_CARESEEKER,
  CREATE_COMPANY,
  CREATE_CONSULTANT,
  CREATE_EMAIL_TRANSLATION,
  CREATE_ENCRYPTION_CONTEXT,
  CREATE_FILE,
  CREATE_INSURANCE,
  CREATE_ONPREMISE_CONFIG,
  CREATE_ONPREMISE_IMPORT,
  CREATE_PATIENT,
  CREATE_PROVIDERSEARCH_CARESEEKER,
  CREATE_PROVIDER_SEARCH_ACCOUNT,
  CREATE_TRANSLATION,
  CREATE_TRANSLATIONS,
  CREATE_USER_MANAGEMENT_FILE,
  DELETE_ACCOUNT_ROLE,
  DELETE_CAREPROVIDER_ACCOUNT_ROLE,
  DELETE_CAREPROVIDER_DPA,
  DELETE_CAREPROVIDER_FILE,
  DELETE_CAREPROVIDER_REVERSE_SEARCH,
  DELETE_CONSULTANT_FILE,
  DELETE_EMAIL_TRANSLATION,
  DELETE_FILE,
  DELETE_PRIORITY_PROVIDER,
  DELETE_SEALD_ACCESS,
  DELETE_TRANSLATION,
  DELETE_USER_MANAGEMENT_FILE,
  FEEDBACK_PATIENT_INFO_MISSING,
  GET_ACCOUNTS,
  GET_ACCOUNTS_FOR_CARESEEKER,
  GET_ACCOUNT_WITHIN_CARESEEKER_GROUP,
  GET_ADDRESS,
  GET_AUCTIONREQUEST,
  GET_CAREPROVIDER,
  GET_CAREPROVIDER_FILES,
  GET_CAREPROVIDER_REVERSE_SEARCHES,
  GET_CARESEEKER,
  GET_CARESEEKER_ACCOUNTS,
  GET_COMPANY,
  GET_CONSULTANT_FILES,
  GET_DYNAMIC_AUCTION_ASSESSMENT_STEPS,
  GET_DYNAMIC_AUCTION_FORM_ALL_FIELDS,
  GET_DYNAMIC_AUCTION_FORM_CONFIGURATION,
  GET_PATIENT,
  GET_RECEIVER_ACCOUNTS,
  GET_SENDER_RECEIVER_NOTE,
  INITIATE_SEALD_IDENTITY,
  LOGIN_PROVIDER_SEARCH_ACCOUNT,
  NOTIFY_RECEIVER_RESPONSIBLE_PERSON,
  NOTIFY_SOCIAL_WORKER,
  POST_ENCRYPTION_JOB,
  REQUEST_BULK_ACTION,
  SEALD_GROUP_RESET,
  SEND_FAX_FOR_AUCTION_REQUEST,
  SEND_TEST_EMAIL,
  START_ELECTIVE_FLOW,
  UPDATE_ACCOUNT,
  UPDATE_ACCOUNT_FOR_CARESEEKER,
  UPDATE_ASSOCIATIONS,
  UPDATE_BROWSER_SUBSCRIPTION,
  UPDATE_CAPACITY,
  UPDATE_CAREPROVIDER,
  UPDATE_CAREPROVIDER_ACCOUNT,
  UPDATE_CAREPROVIDER_ACCOUNT_TOKEN,
  UPDATE_CAREPROVIDER_REVERSE_SEARCH,
  UPDATE_CAREPROVIDER_TOKEN,
  UPDATE_CARESEEKER,
  UPDATE_COMPANY,
  UPDATE_COMPANY_TOKEN,
  UPDATE_CONSULTANT,
  UPDATE_EMAIL_TRANSLATION,
  UPDATE_FILE,
  UPDATE_INSURANCE,
  UPDATE_ONPREMISE_CONFIG,
  UPDATE_PATIENT,
  UPDATE_RECEIVER_SESSION_KEYS,
  UPDATE_SEARCH_CANDIDATES,
  UPDATE_SENDER_RECEIVER_NOTE,
  UPDATE_TRANSLATION,
  UPLOAD_CAREPROVIDER_DPA,
  UPLOAD_CAREPROVIDER_FILE,
  UPLOAD_CONSULTANT_FILE,
  USE_REQUEST_ACTION,
  USE_SEARCH_ACTION,
} from "@recare/core/apollo/graphql";
import { FILE_TYPES, REDIRECT_PARAM, RETURN_PARAM } from "@recare/core/consts";
import { ELIGIBILITY_EVENTS } from "@recare/core/consts/events";
import { getCreateAccountErrorMessage } from "@recare/core/model/accounts/accountErrorHandling";
import { getTermsAndConditions } from "@recare/core/model/careproviders";
import { useApolloEncryptionContext } from "@recare/core/model/patients/encryption/utils";
import { getReferrer } from "@recare/core/model/utils/urls";
import {
  Account,
  AddressCircle,
  AnyObject,
  Auction,
  AuctionRequest,
  BrowserSubscription,
  Candidates,
  Capacity,
  Careprovider,
  CareproviderDpaFile,
  CareproviderFile,
  Careseeker,
  Company,
  Consultant,
  DPAFile,
  DPAFileType,
  EncryptionContext,
  EncryptionJobRequest,
  FaxResponse,
  OauthAuthenticationInput,
  OnPremImport,
  OnpremiseConfigInput,
  Patient,
  ProductTourCompletion,
  QueryProgress,
  RequestActionType,
  SealdAccessPayload,
  SealdIdentityInput,
  SealdIdentityPayload,
  SealdSessionsPayload,
  SearchActionsWithHandlers,
  SenderReceiverNote,
  SessionKey,
  UndoActionType,
} from "@recare/core/types";
import { useTranslations } from "@recare/translations";
import {
  getAuctionRequestEventQuery,
  getPatientEventQuery,
} from "apollo/hooks/queries";
import { useUpdate } from "apollo/mutations/utils";
import { VariablesOf } from "apollo/types";
import { useAuctionRequestEventsContext } from "dsl/atoms/AuctionRequestEventsContext";
import { SendEmail } from "dsl/ecosystems/AcpTestEmail";
import {
  EmailTranslation,
  TranslationType,
  TranslationTypes,
} from "dsl/ecosystems/AcpTranslations/types";
import { useGetDpa } from "dsl/hooks/useDpaLink";
import {
  ReverseSearch,
  ReverseSearches,
} from "dsl/organisms/Filters/ReceiverApp/types";
import { useNotification } from "dsl/organisms/NotificationProvider";
import { useCallback, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  CustomMutationHook,
  GenericMutationType,
  MutationType,
  OperationResult,
  ResetProgressType,
  useGenericMutation,
  useMutationProgress,
  useRefetchOnConflict,
  useTypedGenericMutation,
} from "./utils";

type Error = ApolloError | undefined;
type MutationResult<T> = [
  MutationType<T>,
  QueryProgress,
  ResetProgressType,
  { [querySignature: string]: T } | null | undefined,
  Error,
];
export type GenericMutationResult<T, InputT = T> = [
  GenericMutationType<T, InputT>,
  QueryProgress,
  ResetProgressType,
  { [querySignature: string]: Readonly<T> },
  Error,
];

export function useUpdateTranslation(
  options?: MutationHookOptions,
): MutationResult<TranslationType> {
  const [mutation, { data, error, loading }] = useMutation(
    UPDATE_TRANSLATION,
    options,
  );
  const [updateTranslation, mutationProgress, resetProgress] =
    useMutationProgress<TranslationType>(
      mutation as MutationType<TranslationType>,
      {
        loading,
        error,
      },
    );

  const translation = data?.translation;
  return [
    updateTranslation,
    mutationProgress,
    resetProgress,
    translation,
    error,
  ];
}

export function useCreateTranslation(options?: MutationHookOptions) {
  const [mutation, { data, error, loading }] = useMutation(
    CREATE_TRANSLATION,
    options,
  );
  const [updateTranslation, mutationProgress, resetProgress] =
    useMutationProgress<TranslationType>(
      mutation as MutationType<TranslationType>,
      {
        loading,
        error,
      },
    );

  const mutate = useCallback(
    (input?: Partial<TranslationType> | undefined) =>
      updateTranslation({ variables: { input } }),
    [updateTranslation],
  );

  const translation = data?.translation;
  return [mutate, mutationProgress, resetProgress, translation, error] as const;
}

export function useDeleteTranslation(
  options?: MutationHookOptions,
): MutationResult<TranslationType> {
  const [mutation, { data, error, loading }] = useMutation(
    DELETE_TRANSLATION,
    options,
  );
  const [updateTranslation, mutationProgress, resetProgress] =
    useMutationProgress<TranslationType>(
      mutation as MutationType<TranslationType>,
      {
        loading,
        error,
      },
    );

  const translation = data?.translation;
  return [
    updateTranslation,
    mutationProgress,
    resetProgress,
    translation,
    error,
  ];
}

export function usePostTranslations(
  options?: MutationHookOptions,
): MutationResult<TranslationTypes> {
  const [mutation, { data, error, loading }] = useMutation(
    CREATE_TRANSLATIONS,
    options,
  );
  const [updateTranslations, mutationProgress, resetProgress] =
    useMutationProgress<TranslationTypes>(
      mutation as MutationType<TranslationTypes>,
      {
        loading,
        error,
      },
    );

  const translation = data?.translation;
  return [
    updateTranslations,
    mutationProgress,
    resetProgress,
    translation,
    error,
  ];
}

export function useRequestBulkAction() {
  const [mutation, { data, error, loading }] = useMutation(
    REQUEST_BULK_ACTION,
    {
      refetchQueries: ["careproviderRequestsNew"],
    },
  );
  const [requestBulkAction, requestBulkActionQueryProgress, resetProgress] =
    useMutationProgress<AuctionRequest>(
      mutation as MutationType<AuctionRequest>,
      {
        loading,
        error,
      },
    );

  return [
    requestBulkAction,
    requestBulkActionQueryProgress,
    resetProgress,
    data,
    error,
  ] as const;
}

export const useUpdatePatient: CustomMutationHook<
  typeof UPDATE_PATIENT,
  {
    id: number;
    refetchPatientEligilibityEvents?: boolean;
    withEventLastSeens?: boolean;
  }
> = ({
  id,
  onCompleted,
  onError,
  refetchPatientEligilibityEvents,
  withEventLastSeens,
}) => {
  const encryptionContext = useApolloEncryptionContext();

  const [mutation, mutationProgress, resetProgress, data, error] =
    useTypedGenericMutation(UPDATE_PATIENT, {
      variables: { id },
      context: { encryptionContext },
      refetchQueries: refetchPatientEligilibityEvents
        ? [
            getPatientEventQuery({
              patientId: id,
              eventWhiteList: ELIGIBILITY_EVENTS,
              withEventLastSeens,
            }),
          ]
        : undefined,
      onCompleted,
      onError,
    });

  return [mutation, mutationProgress, resetProgress, data?.patient, error];
};

export function useAddPriorityProvider({
  careseekerId,
  onCompleted,
}: {
  careseekerId: number;
  onCompleted?: () => void;
}): GenericMutationResult<number> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<number>(ADD_PRIORITY_PROVIDER, {
      variables: { careseekerId },
      refetchQueries: ["getCareseeker"],
      onCompleted,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useDeletePriorityProvider({
  careproviderId,
  careseekerId,
  onCompleted,
}: {
  careproviderId: number;
  careseekerId: number;
  onCompleted?: () => void;
}): GenericMutationResult<Careprovider> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<Careprovider>(DELETE_PRIORITY_PROVIDER, {
      variables: { careseekerId, careproviderId },
      onCompleted,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useNotifySocialWorker({
  onCompleted,
  patientId,
}: {
  onCompleted?: () => void;
  patientId: number;
}): GenericMutationResult<{ message: string }> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<{ message: string }>(NOTIFY_SOCIAL_WORKER, {
      variables: { id: patientId },
      onCompleted,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useNotifyReceiverAssignee({
  auctionRequestId,
  onCompleted,
}: {
  auctionRequestId: number;
  onCompleted?: () => void;
}): GenericMutationResult<{ message: string }> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<{ message: string }>(
      NOTIFY_RECEIVER_RESPONSIBLE_PERSON,
      {
        variables: { id: auctionRequestId },
        onCompleted,
      },
    );

  return [mutation, mutationProgress, resetProgress, data, error];
}

export type RejectedProvider = {
  careprovider: Careprovider;
  solution: number;
};

export function useSendFaxForAuctionRequest(): MutationResult<FaxResponse> {
  const [apolloMutation, { data, error, loading }] = useMutation(
    SEND_FAX_FOR_AUCTION_REQUEST,
  );
  const [sendFax, mutationProgress, resetProgress] = useMutationProgress<{
    url: string;
  }>(apolloMutation as MutationType<FaxResponse>, {
    loading,
    error,
  });

  return [sendFax, mutationProgress, resetProgress, data, error];
}

export function useCreateAuction(
  patient?: Patient,
): GenericMutationResult<Auction> {
  const encryptionContext = useApolloEncryptionContext({
    decrypt: true,
  });

  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<Auction>(CREATE_AUCTION, {
      context: { encryptionContext },
      update: (proxy, newAuctionData) => {
        const { auction: newAuction } = newAuctionData?.data || {};
        if (!patient || !newAuction) return;

        const patientWithNewAuction = {
          ...patient,
          auctions: [...(patient.auctions || []), newAuction],
        };

        proxy.writeQuery({
          query: GET_PATIENT,
          variables: { id: patient.id },
          data: {
            patient: patientWithNewAuction,
          },
        });
      },
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useUpdateCompany({
  id,
  onCompleted,
  onError,
  token,
}: {
  id: number;
  onCompleted?: (data: { company: Company }) => void;
  onError?: (error: ApolloError) => void;
  token?: string | undefined;
}): GenericMutationResult<Company> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<Company>(token ? UPDATE_COMPANY_TOKEN : UPDATE_COMPANY, {
      variables: { id, token },
      onCompleted,
      onError,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useUpdateAccount({
  id,
  onCompleted,
}: {
  id?: number;
  onCompleted?: () => void;
} = {}): GenericMutationResult<Account> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<Account>(UPDATE_ACCOUNT, {
      variables: { id },
      onCompleted,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useUpdateCareproviderAccount({
  accountId,
  careproviderId,
  onCompleted,
  token,
}: {
  accountId?: number;
  careproviderId: number;
  onCompleted?: () => void;
  token?: string;
}): MutationResult<Account> {
  const [apolloMutation, { data, error, loading }] = useMutation(
    token ? UPDATE_CAREPROVIDER_ACCOUNT_TOKEN : UPDATE_CAREPROVIDER_ACCOUNT,
    {
      variables: { careproviderId, accountId, token },
      onCompleted,
    },
  );
  const [updateAccount, mutationProgress, resetProgress] =
    useMutationProgress<Account>(apolloMutation as MutationType<Account>, {
      loading,
      error,
    });

  return [updateAccount, mutationProgress, resetProgress, data, error];
}

export function useUpdateReceiverSessionKey(
  patientId: number,
  receiverId: number,
): MutationResult<SessionKey> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation(UPDATE_RECEIVER_SESSION_KEYS, {
      variables: { id: receiverId },
      refetchQueries: [
        {
          query: GET_PATIENT,
          variables: { id: patientId },
        },
      ],
    });

  // @ts-ignore
  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useUpdateReceiversSessionKeys(
  options?: MutationHookOptions,
): MutationResult<Array<SessionKey>> {
  const [mutation, { data, error, loading }] = useMutation(
    UPDATE_RECEIVER_SESSION_KEYS,
    options,
  );

  const [updateSessionKeys, mutationProgress, resetProgress] =
    useMutationProgress<Array<SessionKey>>(
      mutation as MutationType<Array<SessionKey>>,
      {
        loading,
        error,
      },
    );

  const sessionKeys = data?.sessionKeys;
  return [
    updateSessionKeys,
    mutationProgress,
    resetProgress,
    sessionKeys,
    error,
  ];
}

export function useUpdateInsurance({
  insuranceId,
  onCompleted,
}: {
  insuranceId: number;
  onCompleted?: () => void;
}): GenericMutationResult<{ message: string }> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<{ message: string }>(UPDATE_INSURANCE, {
      variables: { id: insuranceId },
      onCompleted,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

type AddAccountRolesResult = {
  account_id: number;
  careprovider_id?: number;
  careseeker_id?: number;
  role_value: number;
}[];

export function useAddAccountRoles({
  careproviderId,
  careseekerId,
  ...options
}: MutationHookOptions<AddAccountRolesResult> & {
  careproviderId?: number;
  careseekerId?: number;
} = {}): GenericMutationResult<AddAccountRolesResult> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<AddAccountRolesResult>(ADD_ACCOUNT_ROLES, {
      update: (cache, { data }) => {
        const queryData = (() => {
          if (careseekerId)
            return {
              query: GET_CARESEEKER_ACCOUNTS,
              variables: { id: careseekerId },
            };
          if (careproviderId)
            return {
              query: GET_RECEIVER_ACCOUNTS,
              variables: { id: careproviderId },
            };

          return {
            query: GET_ACCOUNTS,
            variables: {},
          };
        })();

        const response = cache.readQuery<{ accounts: Account[] }>({
          query: queryData.query,
          variables: queryData.variables,
        });

        const accounts = response?.accounts;
        const newAccount = data?.accountRole;
        const updatedAccounts = accounts?.map((account) =>
          newAccount.id === account.id
            ? { ...account, roles: { ...account.roles, ...newAccount.roles } }
            : account,
        );

        if (newAccount && accounts) {
          cache.writeQuery({
            query: queryData.query,
            variables: queryData.variables,
            data: {
              accounts: updatedAccounts,
            },
          });
        }
      },
      ...options,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useDeleteAccountRoles(): MutationResult<{
  accountId: number;
  queryVars: string;
  role_value: number;
}> {
  const [apolloMutation, { data, error, loading }] =
    useMutation(DELETE_ACCOUNT_ROLE);

  const [deleteRole, mutationProgress, resetProgress] = useMutationProgress<{
    accountId: number;
    queryVars: string;
    role_value: number;
  }>(
    apolloMutation as MutationType<{
      accountId: number;
      queryVars: string;
      role_value: number;
    }>,
    {
      loading,
      error,
    },
  );

  return [deleteRole, mutationProgress, resetProgress, data, error];
}

export function useAddCareproviderAccountRoles({
  careproviderId,
  onCompleted,
}: {
  careproviderId: number;
  onCompleted?: () => void;
}): GenericMutationResult<
  Array<{
    account_id: number;
    careprovider_id: number;
    role_value: number;
  }>
> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      Array<{
        account_id: number;
        careprovider_id: number;
        role_value: number;
      }>
    >(ADD_CAREPROVIDER_ACCOUNT_ROLES, {
      variables: { careproviderId },
      onCompleted,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useDeleteCareproviderAccountRole({
  accountId,
  careproviderId,
  onCompleted,
  queryVars,
  role_value,
}: {
  accountId: number | undefined;
  careproviderId: number;
  onCompleted?: () => void;
  queryVars: string;
  role_value: number;
}): GenericMutationResult<{
  accountId: number;
  careproviderId: number;
  role_value: number;
}> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<{
      accountId: number;
      careproviderId: number;
      queryVars: string;
      role_value: number;
    }>(DELETE_CAREPROVIDER_ACCOUNT_ROLE, {
      variables: { careproviderId, accountId, role_value, queryVars },
      onCompleted,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useCreateInsurance({
  onCompleted,
}: {
  onCompleted?: () => void;
}): GenericMutationResult<{ message: string }> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<{ message: string }>(CREATE_INSURANCE, {
      onCompleted,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useCreateCareseeker({
  onCompleted,
}: {
  onCompleted?: (data: { careseeker: Careseeker }) => void;
}): GenericMutationResult<Careseeker> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<Careseeker>(CREATE_CARESEEKER, {
      onCompleted,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

const updateCompanyCareproviders: MutationUpdaterFunction<
  {
    careprovider: Careprovider;
  },
  any,
  any,
  ApolloCache<{}>
> = (proxy, { data }) => {
  const careprovider = data?.careprovider;
  const companyId = careprovider?.company?.id;
  if (companyId) {
    const getCompany = proxy.readQuery({
      query: GET_COMPANY,
      variables: { id: companyId },
    }) as {
      company: Company;
    } | null;

    const company = getCompany?.company;
    if (company)
      proxy.writeQuery({
        query: GET_COMPANY,
        variables: { id: companyId },
        data: {
          company: {
            ...company,
            careproviders: [...(company?.careproviders ?? []), careprovider],
          },
        },
      });
  }
};

export function useCreateCareprovider({
  onCompleted,
}: {
  onCompleted?: (data: { careprovider: Careprovider }) => void;
}): GenericMutationResult<Careprovider> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<Careprovider>(CREATE_CAREPROVIDER, {
      onCompleted,
      update: updateCompanyCareproviders,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useCreateCompany({
  onCompleted,
}: {
  onCompleted?: (data: { company: Company }) => void;
}): GenericMutationResult<Company> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<Company>(CREATE_COMPANY, {
      onCompleted,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useGetAccountWithinCareseekerGroup({
  careseekerId,
  ...options
}: Pick<MutationHookOptions<Account>, "onCompleted" | "onError"> & {
  careseekerId: number;
}) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<Account, { email: string }>(
      GET_ACCOUNT_WITHIN_CARESEEKER_GROUP,
      {
        variables: { careseekerId },
        ...options,
      },
    );

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useCreateAccountForCareseeker({
  careseekerId,
  ...options
}: Pick<MutationHookOptions<Account>, "onCompleted"> & {
  careseekerId: number;
}) {
  const translations = useTranslations();
  const notify = useNotification();
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<Account>(CREATE_ACCOUNT_FOR_CARESEEKER, {
      variables: { careseekerId },
      update: (cache, { data: { account } }) => {
        // Read the existing accounts from the cache
        const response = cache.readQuery<
          { accounts: Account[] },
          { careseekerId: number }
        >({
          query: GET_ACCOUNTS_FOR_CARESEEKER,
          variables: { careseekerId },
        });

        const accounts = response?.accounts;
        // Update the cache with the new account data
        if (accounts) {
          cache.writeQuery({
            query: GET_ACCOUNTS_FOR_CARESEEKER,
            variables: { careseekerId },
            data: {
              accounts: [...accounts, account],
            },
          });
        }
      },
      onError: (err: any) => {
        notify({
          type: "error",
          message: getCreateAccountErrorMessage({ error: err, translations }),
        });
      },
      ...options,
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useUpdateAccountForCareseeker({
  accountId,
  careseekerId,
  ...options
}: Partial<
  Omit<MutationHookOptions<Account>, "refetchQueries"> & {
    accountId: number;
    careseekerId: number;
  }
> = {}) {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<Account>(UPDATE_ACCOUNT_FOR_CARESEEKER, {
      variables: { accountId, careseekerId },
      ...options,
    });

  return [mutation, mutationProgress, resetProgress, data, error] as const;
}

export function useCreateAccount({
  ...options
}: MutationHookOptions<Account>): GenericMutationResult<Account> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<Account>(CREATE_ACCOUNT, {
      ...options,
    });

  // TODO make sure data.receiverNote is get correctly on use
  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useUpdateCareseeker({
  id,
  onCompleted,
  onError,
}: {
  id: number;
  onCompleted?: (...args: any) => void;
  onError?: (error: ApolloError) => void;
}): GenericMutationResult<Careseeker> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<Careseeker>(UPDATE_CARESEEKER, {
      variables: { id },
      onCompleted,
      onError,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useUpdateCareprovider({
  id,
  onCompleted,
  onError,
  token,
}: {
  id: number;
  onCompleted?: (careprovider: Careprovider) => void;
  onError?: (error: ApolloError) => void;
  token?: string;
}): GenericMutationResult<Careprovider> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<Careprovider>(
      token ? UPDATE_CAREPROVIDER_TOKEN : UPDATE_CAREPROVIDER,
      {
        variables: { id },
        onCompleted,
        onError,
      },
    );

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useArchivePatientMutation({
  id,
  onCompleted,
  onError,
}: {
  id: number;
  onCompleted: () => void;
  onError?: () => void;
}) {
  return useGenericMutation<{ archived: boolean; id: number }>(
    ARCHIVE_PATIENT,
    {
      variables: { id },
      onCompleted,
      onError,
    },
  );
}

export function useGetDynamicAuctionFormSteps({
  revision,
}: {
  revision: number;
}) {
  const [mutation, { data, error, loading }] = useMutation(
    GET_DYNAMIC_AUCTION_ASSESSMENT_STEPS,
    {
      variables: { revision },
    },
  );
  const [updateFormScopes, queryProgress, resetProgress] = useMutationProgress<
    Array<string>
  >(mutation as MutationType<Array<string>>, {
    loading,
    error,
  });

  return [updateFormScopes, queryProgress, resetProgress, data, error] as const;
}

export function useGetDynamicAuctionFormConfiguration({
  isOnePageAssessment,
  revision,
}: {
  isOnePageAssessment?: boolean;
  revision: number;
}) {
  const [mutation, { data, error, loading }] = useMutation(
    isOnePageAssessment
      ? GET_DYNAMIC_AUCTION_FORM_ALL_FIELDS
      : GET_DYNAMIC_AUCTION_FORM_CONFIGURATION,
    {
      variables: { revision: isOnePageAssessment ? undefined : revision },
    },
  );
  const [updateFormScopes, queryProgress, resetProgress] = useMutationProgress<
    Array<string>
  >(mutation as MutationType<Array<string>>, {
    loading,
    error,
  });

  return [updateFormScopes, queryProgress, resetProgress, data, error] as const;
}

export function useOauthAuthentication({
  careproviderId,
  onCompleted,
  onError,
}: {
  careproviderId: number;
  onCompleted?: ({ onpremiseConfig }: { onpremiseConfig: "success" }) => void;
  onError?: (error: ApolloError) => void;
}): GenericMutationResult<OauthAuthenticationInput> {
  return useGenericMutation<OauthAuthenticationInput>(CONNEXT_AUTHENTICATION, {
    variables: { careproviderId },
    onCompleted,
    onError,
    refetchQueries: [
      { query: GET_CAREPROVIDER({}), variables: { id: careproviderId } },
    ],
  });
}

export function useUpsertOnpremiseConfig({
  configId,
  onCompleted,
  onError,
}: {
  configId: number;
  onCompleted?: (...args: any) => void;
  onError?: (error: ApolloError) => void;
}): GenericMutationResult<OnpremiseConfigInput> {
  const isNew = configId === -1;
  const UPSERT_ONPREMISE_CONFIG = isNew
    ? CREATE_ONPREMISE_CONFIG
    : UPDATE_ONPREMISE_CONFIG;

  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<OnpremiseConfigInput>(UPSERT_ONPREMISE_CONFIG, {
      variables: isNew ? undefined : { configId },
      onCompleted,
      onError,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useCreateOnPremImport({
  careseekerId,
  onCompleted,
  onError,
}: {
  careseekerId: number;
  onCompleted?: (...args: any) => void;
  onError?: (error: ApolloError) => void;
}): GenericMutationResult<OnPremImport> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<OnPremImport>(CREATE_ONPREMISE_IMPORT, {
      variables: { careseekerId },
      onCompleted,
      onError,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useGetAddressMutation(args?: {
  onCompleted?: (data?: any) => void;
}): GenericMutationResult<AddressCircle> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<AddressCircle>(GET_ADDRESS, {
      onCompleted: args?.onCompleted,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useCreateFeedbackPatientInfoMissing({
  auctionRequestId,
  careproviderId,
  onCompleted,
  refetchPatientEvents,
}: {
  auctionRequestId: number | null | undefined;
  careproviderId: number | null | undefined;
  onCompleted?: () => void;
  refetchPatientEvents?: boolean;
}): GenericMutationResult<string> {
  const refetchQueries = [];

  if (auctionRequestId && refetchPatientEvents) {
    refetchQueries.push(
      getAuctionRequestEventQuery({
        auctionRequestId,
      }),
    );
  }
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<string>(FEEDBACK_PATIENT_INFO_MISSING, {
      variables: { careproviderId, auctionRequestId },
      onCompleted,
      refetchQueries: refetchQueries.length ? refetchQueries : [],
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useCreateSenderReceiverNote({
  careproviderId,
  careseekerId,
  onCompleted,
}: {
  careproviderId: number | null | undefined;
  careseekerId: number | null | undefined;
  onCompleted?: () => void;
}): GenericMutationResult<SenderReceiverNote> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<SenderReceiverNote>(UPDATE_SENDER_RECEIVER_NOTE, {
      variables: { careseekerId, careproviderId },
      onCompleted,
      update: (proxy, { data }) => {
        proxy.writeQuery({
          query: GET_SENDER_RECEIVER_NOTE,
          variables: { careseekerId, careproviderId },
          data,
        });
      },
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useUpdateAssociations(): MutationResult<any> {
  const [apolloMutation, { data, error, loading }] =
    useMutation(UPDATE_ASSOCIATIONS);
  const [mutation, mutationProgress, resetProgress] = useMutationProgress<{
    id: any;
  }>(apolloMutation as MutationType<any>, {
    loading,
    error,
  });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useUploadCareproviderDpa({
  careproviderId,
  fileType,
}: {
  careproviderId: number;
  fileType: number;
}): GenericMutationResult<DPAFileType> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<DPAFileType>(UPLOAD_CAREPROVIDER_DPA, {
      variables: {
        careproviderId,
        fileType,
      },
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useDeleteCareproviderDpa({
  careproviderId,
}: {
  careproviderId: number;
}) {
  const [mutation, { data, error, loading }] = useMutation(
    DELETE_CAREPROVIDER_DPA,
    {
      variables: { careproviderId },
      refetchQueries: [
        { query: GET_CAREPROVIDER({}), variables: { id: careproviderId } },
      ],
    },
  );
  const [deleteDpaFile, mutationProgress, resetProgress] =
    useMutationProgress<CareproviderDpaFile>(
      mutation as MutationType<CareproviderDpaFile>,
      {
        loading,
        error,
      },
    );

  return [deleteDpaFile, mutationProgress, resetProgress, data, error] as const;
}

export function useUploadCareproviderFile({
  careproviderId,
  careproviderToken,
  fileType,
}: {
  careproviderId: number;
  careproviderToken?: string;
  fileType: number;
}): GenericMutationResult<CareproviderFile> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<CareproviderFile>(UPLOAD_CAREPROVIDER_FILE, {
      variables: {
        careproviderId,
        params: careproviderToken ? `?token=${careproviderToken}` : "",
        fileType,
      },
      refetchQueries: [
        {
          query: GET_CAREPROVIDER_FILES,
          variables: {
            careproviderId,
            params: careproviderToken ? `?token=${careproviderToken}` : "",
          },
        },
      ],
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useDeleteCareproviderFile({
  careproviderId,
  careproviderToken,
}: {
  careproviderId: number;
  careproviderToken?: string;
}): [
  MutationType<CareproviderFile>,
  QueryProgress,
  ResetProgressType,
  ApolloError | undefined,
] {
  const [mutation, { error, loading }] = useMutation(DELETE_CAREPROVIDER_FILE, {
    variables: {
      careproviderId,
      params: careproviderToken ? `?token=${careproviderToken}` : "",
    },
    refetchQueries: [
      {
        query: GET_CAREPROVIDER_FILES,
        variables: {
          careproviderId,
          params: careproviderToken ? `?token=${careproviderToken}` : "",
        },
      },
    ],
  });
  const [deleteFile, mutationProgress, resetProgress] =
    useMutationProgress<CareproviderFile>(
      mutation as MutationType<CareproviderFile>,
      {
        loading,
        error,
      },
    );

  return [deleteFile, mutationProgress, resetProgress, error];
}

export const useCreateUserManagementFile: CustomMutationHook<
  typeof CREATE_USER_MANAGEMENT_FILE,
  VariablesOf<typeof CREATE_USER_MANAGEMENT_FILE>
> = () => {
  const [mutation, mutationProgres, resetProgress, data, error] =
    useTypedGenericMutation(CREATE_USER_MANAGEMENT_FILE, {
      refetchQueries: ["getCareseeker", "getAccounts"],
    });

  return [mutation, mutationProgres, resetProgress, data?.file, error];
};

export const useDeleteUserManagementFile: CustomMutationHook<
  typeof DELETE_USER_MANAGEMENT_FILE,
  VariablesOf<typeof DELETE_USER_MANAGEMENT_FILE>
> = ({ id }) => {
  const [mutation, mutationProgres, resetProgress, data, error] =
    useTypedGenericMutation(DELETE_USER_MANAGEMENT_FILE, {
      variables: { id },
      refetchQueries: ["getCareseeker", "getAccounts"],
    });

  return [mutation, mutationProgres, resetProgress, data?.file, error];
};

export const useCreateFileMutation: CustomMutationHook<
  typeof CREATE_FILE,
  VariablesOf<typeof CREATE_FILE>
> = ({ params }) => {
  const [mutation, mutationProgres, resetProgress, data, error] =
    useTypedGenericMutation(CREATE_FILE, {
      variables: { params },
    });

  return [mutation, mutationProgres, resetProgress, data?.file, error];
};

export const useUpdateFileMutation: CustomMutationHook<
  typeof UPDATE_FILE,
  VariablesOf<typeof UPDATE_FILE>
> = ({ id, params }) => {
  const { eventsQuery } = useAuctionRequestEventsContext();
  const [mutation, mutationProgress, resetProgress, data, error] =
    useTypedGenericMutation(UPDATE_FILE, {
      variables: { id, params },
      refetchQueries: eventsQuery ? [eventsQuery] : undefined,
    });

  return [mutation, mutationProgress, resetProgress, data?.file, error];
};

export const useDeleteFileMutation: CustomMutationHook<
  typeof DELETE_FILE,
  VariablesOf<typeof DELETE_FILE>
> = ({ id, onCompleted, onError, params }) => {
  const { eventsQuery } = useAuctionRequestEventsContext();
  const [mutation, mutationProgress, resetProgress, data, error] =
    useTypedGenericMutation(DELETE_FILE, {
      variables: { id, params },
      onCompleted,
      onError,
      refetchQueries: ["getFiles", ...(eventsQuery ? [eventsQuery] : [])],
    });

  return [mutation, mutationProgress, resetProgress, data?.file, error];
};

export function useRequestActionMutation({
  actionType,
  onCompleted,
  onError,
  refetchQueries,
  requestId,
}: {
  actionType: RequestActionType | UndoActionType;
  onCompleted?: (data: AuctionRequest) => void;
  onError?: (error: ApolloError) => void;
  refetchQueries?: BaseMutationOptions["refetchQueries"];
  requestId?: number;
}): GenericMutationResult<
  AuctionRequest,
  {
    context: AnyObject;
    ref: string;
    request_id: number;
  }
> {
  const refetch = useRefetchOnConflict();
  const encryptionContext = useApolloEncryptionContext({ decrypt: true });

  const memoisedMutation = useMemo(
    () => USE_REQUEST_ACTION(actionType),
    [actionType, requestId],
  );

  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      AuctionRequest,
      {
        context: AnyObject;
        ref: string;
        request_id: number;
      }
    >(memoisedMutation, {
      ...({ refetchQueries } || {}),
      onError: (err) => {
        if (requestId && requestId > 0) {
          refetch({
            error: err,
            query: GET_AUCTIONREQUEST,
            variables: { id: requestId, ref: getReferrer() },
            context: { encryptionContext },
          });
        } else {
          console.error(
            `[DEV-14068] [useRefetchOnConflict] tried to access bad request id ${requestId}`,
          );
        }
        onError?.(err);
      },

      onCompleted,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useSearchActionMutation({
  actionType,
  refetchQueries,
}: {
  actionType: SearchActionsWithHandlers;
  refetchQueries?: BaseMutationOptions["refetchQueries"];
}): GenericMutationResult<
  Auction,
  {
    auction_id: number;
    context: AnyObject;
    ref: string;
  }
> {
  const encryptionContext = useApolloEncryptionContext();

  const memoisedMutation = useMemo(
    () => USE_SEARCH_ACTION(actionType),
    [actionType],
  );

  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<
      Auction,
      {
        auction_id: number;
        context: AnyObject;
        ref: string;
      }
    >(memoisedMutation, {
      context: { encryptionContext },
      ...({ refetchQueries } || {}),
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useAcceptTermsAndConditions({
  careprovider,
}: {
  careprovider: Careprovider | undefined;
}): [() => OperationResult<Careprovider> | null, QueryProgress] {
  const [update, progress] = useUpdateCareprovider({
    id: careprovider?.id ?? -1,
  });

  const updateTC = () => {
    if (!careprovider) {
      console.error("careprovider undefined in useAcceptTermsAndConditions");
      return null;
    }
    return update({
      terms_and_conditions: getTermsAndConditions(careprovider),
    });
  };

  return [updateTC, progress];
}

export function useAcceptDPA({
  careproviderId,
  isChurchOwned,
}: {
  careproviderId: number;
  isChurchOwned: boolean;
}): [(input?: DPAFile) => OperationResult<DPAFileType>, QueryProgress] {
  const currentVersion = useGetDpa().version;

  const [uploadDpaFile, mutationProgress] = useUploadCareproviderDpa({
    careproviderId,
    fileType: FILE_TYPES.DOCUMENTS,
  });
  const updateDPA = useCallback(
    (input?: Partial<DPAFileType>) =>
      uploadDpaFile({
        ...input,
        church_owned: isChurchOwned,
        version: currentVersion,
      }),
    [uploadDpaFile],
  );
  return [updateDPA, mutationProgress];
}

export function usePostEncryptionJob(): GenericMutationResult<
  null,
  EncryptionJobRequest
> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<null, EncryptionJobRequest>(POST_ENCRYPTION_JOB, {});

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useCreateEncryptionContext(): GenericMutationResult<EncryptionContext> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<EncryptionContext>(CREATE_ENCRYPTION_CONTEXT, {});

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useInitiateSealdIdentity(): GenericMutationResult<
  SealdIdentityPayload,
  SealdIdentityInput
> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<SealdIdentityPayload, SealdIdentityInput>(
      INITIATE_SEALD_IDENTITY,
      {},
    );

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useUpdateBrowserSubscription({
  id,
  onCompleted,
}: {
  id?: number;
  onCompleted?: () => void;
}): GenericMutationResult<BrowserSubscription> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<BrowserSubscription>(UPDATE_BROWSER_SUBSCRIPTION, {
      variables: { id },
      onCompleted,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useStartElectiveFlow() {
  return useGenericMutation(START_ELECTIVE_FLOW, {});
}

export function useDeleteSealdAccess(): GenericMutationResult<
  null,
  SealdAccessPayload
> {
  return useGenericMutation(DELETE_SEALD_ACCESS, {});
}

export function useCreateReverseSearch({
  careproviderId,
  onCompleted,
  onError,
}: {
  careproviderId: number;
  onCompleted?: MutationHookOptions<ReverseSearch>["onCompleted"];
  onError?: MutationHookOptions<ReverseSearch>["onError"];
}): GenericMutationResult<ReverseSearch> {
  return useGenericMutation<ReverseSearch>(CREATE_CAREPROVIDER_REVERSE_SEARCH, {
    onCompleted,
    onError,
    variables: { careproviderId },
    refetchQueries: [
      {
        query: GET_CAREPROVIDER_REVERSE_SEARCHES,
        variables: { careproviderId },
      },
    ],
  });
}

export function useDeleteReverseSearch({
  careproviderId,
  onCompleted,
  onError,
  searchId,
}: {
  careproviderId: number | undefined;
  onCompleted?: MutationHookOptions<ReverseSearch>["onCompleted"];
  onError?: MutationHookOptions<ReverseSearch>["onError"];
  searchId: number | undefined;
}): GenericMutationResult<ReverseSearches> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<ReverseSearches>(DELETE_CAREPROVIDER_REVERSE_SEARCH, {
      variables: { searchId, careproviderId },
      onCompleted,
      onError,
      refetchQueries: [
        {
          query: GET_CAREPROVIDER_REVERSE_SEARCHES,
          variables: { careproviderId },
        },
      ],
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useUpdateReverseSearch({
  careproviderId,
  onCompleted,
  onError,
  searchId,
}: {
  careproviderId: number | undefined;
  onCompleted?: MutationHookOptions<ReverseSearch>["onCompleted"];
  onError?: MutationHookOptions<ReverseSearch>["onError"];
  searchId?: number | undefined;
}): GenericMutationResult<ReverseSearch> {
  return useGenericMutation<ReverseSearch>(UPDATE_CAREPROVIDER_REVERSE_SEARCH, {
    onCompleted,
    onError,
    variables: { searchId, careproviderId },
    refetchQueries: [
      {
        query: GET_CAREPROVIDER_REVERSE_SEARCHES,
        variables: { careproviderId },
      },
    ],
  });
}

export function useUpdateSearchCandidates() {
  const variables = useUpdate();
  return useGenericMutation<Candidates, Auction>(UPDATE_SEARCH_CANDIDATES, {
    variables,
  });
}
export function useCompleteProductTour(tourId: number | undefined) {
  return useGenericMutation<
    ProductTourCompletion,
    { completion_rate: number; step_id: number }
  >(COMPLETE_PRODUCT_TOUR, { variables: { tourId } });
}

export function useCheckSealdSessions() {
  return useGenericMutation<
    SealdSessionsPayload,
    {
      auction_request_id?: number;
      file_id?: number;
      patient_id?: number;
    }
  >(CHECK_SEALD_SESSIONS, {});
}

const useGetProviderSearchRedirectOnLogin = () => {
  const [search] = useSearchParams();
  const location = useLocation();

  search.delete("challenge");
  search.delete(REDIRECT_PARAM);

  // param used for if a user accessed an authenticated route whilst logged out
  // this is passed to the BE so they can create a log in link with tokens for the user
  // to access the page they intended from their email
  const zipcodeRedirect = search.get(RETURN_PARAM);
  if (zipcodeRedirect) {
    return zipcodeRedirect;
  }

  const searchString = search.toString();

  return encodeURIComponent(
    `${location.pathname}${searchString?.length ? `?${searchString}` : ""}`,
  );
};

export function useCreateProviderSearchAccount({
  onCompleted,
  onError,
}: {
  onCompleted: () => void;
  onError: (error: ApolloError) => void;
}): GenericMutationResult<null, { email: string }> {
  const redirectTo = useGetProviderSearchRedirectOnLogin();
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<null, { email: string }>(
      CREATE_PROVIDER_SEARCH_ACCOUNT,
      {
        variables: { redirectTo },
        onError,
        onCompleted,
      },
    );

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useLoginProviderSearch({
  onCompleted,
  onError,
}: {
  onCompleted: () => void;
  onError: (error: ApolloError) => void;
}): GenericMutationResult<null, { email: string }> {
  const redirectTo = useGetProviderSearchRedirectOnLogin();
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<null, { email: string }>(LOGIN_PROVIDER_SEARCH_ACCOUNT, {
      variables: { redirectTo },
      onError,
      onCompleted,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useCreatePatientMutation(): GenericMutationResult<Patient> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<Patient>(CREATE_PATIENT, {});

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useCreateProviderSearchCareseeker(): GenericMutationResult<Careseeker> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<Careseeker>(CREATE_PROVIDERSEARCH_CARESEEKER, {
      update: (proxy, { data: { careseeker } = {} }) => {
        const variables = { id: careseeker.id };
        proxy.writeQuery({
          // should match query signiture in useLoggedCareseeker
          query: GET_CARESEEKER({ withAdminFields: true, withConfig: false }),
          variables,
          data: {
            careseeker,
          },
        });
      },
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useArchiveProviderSearchPatient({
  id,
  onCompleted,
}: {
  id: number;
  onCompleted: MutationHookOptions["onCompleted"];
}): GenericMutationResult<{
  id: number;
}> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<{ id: number }>(ARCHIVE_PROVIDER_SEARCH_PATIENT, {
      variables: { id },
      refetchQueries: [
        "getProviderSearchRequestOverview",
        "getProviderSearchPatients",
      ],
      onCompleted,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useSealdGroupReset(): GenericMutationResult<
  null,
  { seald_id: string }
> {
  return useGenericMutation(SEALD_GROUP_RESET, {
    refetchQueries: ["getAccounts"],
  });
}

export function useUpdateConsultant({
  id,
}: {
  id: number;
}): GenericMutationResult<Consultant> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<Consultant>(UPDATE_CONSULTANT, {
      variables: { id },
      refetchQueries: ["getConsultantEventPage"],
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useDeleteConsultantFile({
  consultantId,
}: {
  consultantId: number;
}): [
  MutationType<CareproviderFile>,
  QueryProgress,
  ResetProgressType,
  ApolloError | undefined,
] {
  const [mutation, { error, loading }] = useMutation(DELETE_CONSULTANT_FILE, {
    variables: {
      consultantId,
    },
    refetchQueries: [
      "getConsultantEventPage",
      {
        query: GET_CONSULTANT_FILES,
        variables: {
          consultantId,
        },
      },
    ],
  });
  const [deleteFile, mutationProgress, resetProgress] =
    useMutationProgress<CareproviderFile>(
      mutation as MutationType<CareproviderFile>,
      {
        loading,
        error,
      },
    );

  return [deleteFile, mutationProgress, resetProgress, error];
}

export function useUploadConsultantFile({
  consultantId,
}: {
  consultantId: number;
}): GenericMutationResult<CareproviderFile> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<CareproviderFile>(UPLOAD_CONSULTANT_FILE, {
      variables: {
        consultantId,
        fileType: FILE_TYPES.IMAGES,
      },
      refetchQueries: [
        "getConsultantEventPage",
        {
          query: GET_CONSULTANT_FILES,
          variables: {
            consultantId,
          },
        },
      ],
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useCreateConsultant({
  onCompleted,
}: {
  onCompleted?: (data: { consultant: Consultant }) => void;
}): GenericMutationResult<Consultant> {
  const [mutation, mutationProgress, resetProgress, data, error] =
    useGenericMutation<Consultant>(CREATE_CONSULTANT, {
      onCompleted,
    });

  return [mutation, mutationProgress, resetProgress, data, error];
}

export function useUpdateEmailTranslation(
  options?: MutationHookOptions,
): MutationResult<EmailTranslation> {
  const [mutation, { data, error, loading }] = useMutation(
    UPDATE_EMAIL_TRANSLATION,
    options,
  );
  const [updateTranslation, mutationProgress, resetProgress] =
    useMutationProgress<EmailTranslation>(
      mutation as MutationType<EmailTranslation>,
      {
        loading,
        error,
      },
    );

  const translation = data?.translation;
  return [
    updateTranslation,
    mutationProgress,
    resetProgress,
    translation,
    error,
  ];
}

export function useCreateEmailTranslation(
  options?: MutationHookOptions,
): MutationResult<EmailTranslation> {
  const [mutation, { data, error, loading }] = useMutation(
    CREATE_EMAIL_TRANSLATION,
    options,
  );
  const [updateTranslation, mutationProgress, resetProgress] =
    useMutationProgress<EmailTranslation>(
      mutation as MutationType<EmailTranslation>,
      {
        loading,
        error,
      },
    );

  const mutate = useCallback(
    (input: any) => updateTranslation({ variables: { input } }),
    [updateTranslation],
  );

  const translation = data?.translation;
  return [mutate, mutationProgress, resetProgress, translation, error];
}

export function useDeleteEmailTranslation(
  options?: MutationHookOptions,
): MutationResult<TranslationType> {
  const [mutation, { data, error, loading }] = useMutation(
    DELETE_EMAIL_TRANSLATION,
    options,
  );
  const [updateTranslation, mutationProgress, resetProgress] =
    useMutationProgress<TranslationType>(
      mutation as MutationType<TranslationType>,
      {
        loading,
        error,
      },
    );

  const translation = data?.translation;
  return [
    updateTranslation,
    mutationProgress,
    resetProgress,
    translation,
    error,
  ];
}

export function useSendTestEmail(
  options?: MutationHookOptions,
): [MutationType<SendEmail>, QueryProgress, ResetProgressType] {
  const [mutation, { error, loading }] = useMutation(SEND_TEST_EMAIL, options);
  const [sendEmail, progress, resetProgress] = useMutationProgress<SendEmail>(
    mutation as MutationType<SendEmail>,
    {
      loading,
      error,
    },
  );

  return [sendEmail, progress, resetProgress];
}

export function useUpdateCapacity({
  careprovider,
  options,
}: {
  careprovider: Careprovider;
  options?: MutationHookOptions;
}) {
  const client = useApolloClient();
  const [mutation, { error, loading }] = useMutation<{ capacity: Capacity }>(
    UPDATE_CAPACITY,
    {
      ...options,
      onCompleted: (data) => {
        if (data) {
          client.writeQuery({
            query: GET_CAREPROVIDER({}),
            data: {
              careprovider: {
                ...careprovider,
                profile: {
                  ...careprovider.profile,
                  capacity: data.capacity,
                },
                __typename: "Careprovider",
              },
            },
          });
        }
        options?.onCompleted?.(data);
      },
    },
  );
  const [mutate, mutationProgress] = useMutationProgress<Capacity>(
    mutation as MutationType<Capacity>,
    {
      loading,
      error,
    },
  );

  const confirmCapacity = () =>
    mutate({
      variables: {
        id: careprovider.id,
        input: {
          capacity_confirmed: true,
        },
      },
    });

  const updateCapacity = (capacity: Omit<Capacity, "capacity_confirmed">) =>
    mutate({
      variables: {
        id: careprovider.id,
        input: capacity,
      },
    });

  return { updateCapacity, confirmCapacity, mutationProgress };
}
